import { useQuery } from '@tanstack/react-query';
import type { Bilingue, EtatExposition, StatutOeuvre, TypeDocument } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import type { EtatDemande } from './demandes.js';
import type { EtatPublication } from './site.js';
import { appelDeRequete, cleMetier } from './cles.js';

/**
 * Tableau de bord — `/v1/g/:slug/tableau-de-bord` (`docs/dev/api.md` § 20,
 * S3-g). Les formes suivent `apps/api/src/tableau-de-bord/agregation.ts` au
 * champ près, comme partout ailleurs.
 *
 * UN SEUL appel remplace les six requêtes de listes que la page faisait —
 * dont une qui chargeait jusqu'à deux cents œuvres pour afficher quatre
 * nombres. Les compteurs sont désormais ceux de la base, donc justes au-delà
 * d'une page ; les listes portent exactement les cinq lignes affichées.
 *
 * Une section ABSENTE et une section VIDE ne disent pas la même chose : le
 * serveur omet celles dont le module est fermé ou la lecture refusée, et la
 * page ne monte pas la tuile correspondante. Aucun recalcul côté client.
 */

export interface DemandeDuTableau {
  readonly id: string;
  readonly nom: string;
  readonly sujet: string;
  readonly etat: EtatDemande;
  readonly oeuvreId: string | null;
  readonly recueLe: string;
}

export interface SectionDemandes {
  readonly aTraiter: number;
  readonly dernieres: readonly DemandeDuTableau[];
}

export interface ReservationDuTableau {
  readonly id: string;
  readonly oeuvreId: string;
  readonly oeuvreTitre: string;
  readonly contactId: string;
  readonly jusquau: string;
}

export interface SectionReservations {
  readonly actives: number;
  readonly expirentBientot: number;
  readonly prochaines: readonly ReservationDuTableau[];
}

export interface ExpositionDuTableau {
  readonly id: string;
  readonly titre: Bilingue;
  readonly etat: EtatExposition;
  readonly debutLe: string;
  readonly finLe: string;
}

export interface SectionExpositions {
  readonly enCours: number;
  readonly aVenir: number;
  readonly prochaines: readonly ExpositionDuTableau[];
}

export interface OeuvreDuTableau {
  readonly id: string;
  readonly titre: string;
  readonly artisteNom: string;
  readonly statut: StatutOeuvre;
  readonly creeLe: string;
}

export interface SectionOeuvres {
  readonly total: number;
  /** Tous les statuts, y compris ceux à zéro : l'ordre d'affichage est à nous. */
  readonly parStatut: Readonly<Record<StatutOeuvre, number>>;
  readonly dernieres: readonly OeuvreDuTableau[];
  /**
   * L'œuvre à mettre en avant faute de réservation qui prime : jamais
   * `dernieres[0]` pris tel quel (§ 20.2) — le serveur a déjà écarté les
   * œuvres archivées et celles sans média. `null` si aucune n'est éligible :
   * la page n'affiche alors aucun bloc image.
   */
  readonly vedette: OeuvreDuTableau | null;
}

export interface EcheanceDuTableau {
  readonly id: string;
  readonly titre: string;
  readonly type: TypeDocument;
  readonly echeanceLe: string;
  readonly joursRestants: number;
}

export interface SectionCoffre {
  readonly enRetard: number;
  readonly sousTrenteJours: number;
  readonly prochaines: readonly EcheanceDuTableau[];
  /** Documents vivants, archives exclues (§ 20.2). */
  readonly total: number;
  /** Les types absents sont omis : l'absence vaut zéro. */
  readonly parType: Readonly<Partial<Record<TypeDocument, number>>>;
}

export interface SectionSite {
  oeuvresIncompletes?: number;
  readonly derniere: {
    readonly version: number;
    readonly date: string;
    readonly etat: EtatPublication;
  } | null;
}

export interface TableauDeBord {
  readonly dormantes?: { readonly total: number };
  readonly genereLe: string;
  readonly introuvables?: {
    total: number;
    dernieres: {
      id: string;
      oeuvreId: string;
      exemplaireId: string | null;
      titre: string;
      note: string;
      declareeLe: string;
    }[];
  };
  readonly registrePolice?: { readonly propositionsEnAttente: number };
  readonly demandes?: SectionDemandes;
  readonly reservations?: SectionReservations;
  readonly expositions?: SectionExpositions;
  readonly oeuvres?: SectionOeuvres;
  readonly coffre?: SectionCoffre;
  readonly site?: SectionSite;
}

export const tableauDeBordApi = {
  lire: (galerie: string, appel?: AppelApi) =>
    api.get<TableauDeBord>(`/g/${encodeURIComponent(galerie)}/tableau-de-bord`, {
      galerie,
      ...appel,
    }),
};

// ── Hooks React Query ────────────────────────────────────────────────────────

export const cleTableauDeBord = (galerie: string) => cleMetier(galerie, 'tableau-de-bord');

export function useTableauDeBord(galerie: string) {
  return useQuery({
    queryKey: cleTableauDeBord(galerie),
    queryFn: (contexte) => tableauDeBordApi.lire(galerie, appelDeRequete(contexte)),
  });
}
